import { useState } from 'react';
import {
  Check, Loader2, AlertTriangle, X,
  Shield, GitBranch, Database, Wrench, Brain, Zap,
  ChevronRight,
} from 'lucide-react';
import type { NormalizedThinkingEvent } from './types';
import { formatDuration } from './normalizeThinkingEvent';

const PHASE_ICON: Record<string, React.ElementType> = {
  safety: Shield,
  planning: GitBranch,
  data_fetch: Database,
  tools: Wrench,
  agents: Brain,
  finalization: Zap,
  system: Zap,
};

interface ThinkingStepItemProps {
  event: NormalizedThinkingEvent;
  isLast: boolean;
}

export function ThinkingStepItem({ event, isLast }: ThinkingStepItemProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const PhaseIcon = PHASE_ICON[event.phase] || Zap;
  const isRunning = event.status === 'running';
  const isError = event.status === 'error' || event.status === 'blocked';
  const isWarning = event.status === 'warning';
  const isDone = event.status === 'success';

  const hasToolResult = event.metadata?.tool_result != null;
  const toolResultMsg = hasToolResult
    ? (event.metadata.tool_result as Record<string, string>)?.message
    : null;

  return (
    <div
      className={`thinking-step-item group ${isLast && isRunning ? 'thinking-step-active' : ''}`}
    >
      <div className="flex items-start gap-2 py-1 px-1.5 rounded-md
        hover:bg-surface-hover/30 transition-colors cursor-default">
        {/* Status indicator */}
        <div className="mt-0.5 shrink-0">
          {isRunning ? (
            <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
          ) : isError ? (
            <X className="w-3.5 h-3.5 text-red-500" />
          ) : isWarning ? (
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          ) : (
            <Check className="w-3.5 h-3.5 text-emerald-500 thinking-check-appear" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <PhaseIcon className="w-3 h-3 text-text-tertiary/50 shrink-0" />
            <span className={`text-[11px] leading-tight truncate ${
              isRunning ? 'text-foreground font-medium' : 'text-text-tertiary'
            }`}>
              {event.title}
            </span>
          </div>

          {/* Progress bar for running items */}
          {isRunning && event.progressPercent != null && (
            <div className="mt-1 h-0.5 w-full bg-border/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary/60 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${Math.min(100, event.progressPercent)}%` }}
              />
            </div>
          )}

          {/* Tool result preview */}
          {isDone && toolResultMsg && (
            <button
              type="button"
              onClick={() => setDetailOpen(!detailOpen)}
              className="mt-0.5 flex items-center gap-1 text-[10px] text-text-tertiary/50
                hover:text-text-tertiary transition-colors"
            >
              <ChevronRight className={`w-2.5 h-2.5 transition-transform duration-150
                ${detailOpen ? 'rotate-90' : ''}`} />
              <span className="truncate max-w-[280px]">
                {toolResultMsg.length > 80 ? toolResultMsg.slice(0, 80) + '…' : toolResultMsg}
              </span>
            </button>
          )}

          {detailOpen && toolResultMsg && (
            <pre className="mt-1 p-2 rounded-md bg-surface-sunken/40 text-[10px] text-text-tertiary
              font-mono whitespace-pre-wrap wrap-break-word max-h-28 overflow-y-auto leading-relaxed">
              {toolResultMsg}
            </pre>
          )}
        </div>

        {/* Duration */}
        {event.durationMs != null && event.durationMs > 0 && (
          <span className="text-[10px] tabular-nums text-text-tertiary/40 shrink-0 mt-0.5">
            {formatDuration(event.durationMs)}
          </span>
        )}
      </div>
    </div>
  );
}
